import React, { useMemo, useState } from 'react';
import {
  Alert, Box, Button, FormControlLabel, IconButton, InputAdornment, ListItemIcon, ListItemText, Menu, MenuItem, Paper,
  Stack, Switch, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography, alpha, useTheme,
} from '@mui/material';
import { Add, EditOutlined, MoreVert, Search, ToggleOffOutlined, ToggleOnOutlined } from '@mui/icons-material';
import { elegantMenuProps } from '../../../utils/menuStyles';
import { useSnackbar } from 'notistack';
import financeService from '../../../services/financeService';
import { LedgerAccount } from '../../../types/finance';
import { useModulePermissions } from '../../../hooks/useModulePermissions';
import { ToneChip } from '../../common/StatusChip';
import TableRowSkeletons from '../../common/TableRowSkeletons';
import Amount from '../ledger/Amount';
import AccountLedgerLink from '../ledger/AccountLedgerLink';
import { useChart } from '../ledger/useChart';
import AccountDialog from './AccountDialog';

const CONTROL_LABEL: Record<string, string> = { FEES: 'Fees module', FINES: 'Fines module', PAYABLES: 'Payables module' };

/**
 * The chart as a tree with live balances. Reserved accounts stay hidden
 * until the school switches them on; nothing is ever deleted.
 */
const ChartOfAccountsPanel: React.FC = () => {
  const theme = useTheme();
  const { canWrite } = useModulePermissions();
  const { enqueueSnackbar } = useSnackbar();
  const { accounts, loading, error, reload } = useChart();
  const [q, setQ] = useState('');
  const [showOff, setShowOff] = useState(false);
  const [dialog, setDialog] = useState<{ account: LedgerAccount | null } | null>(null);
  const [menu, setMenu] = useState<{ anchor: HTMLElement; account: LedgerAccount } | null>(null);
  const editable = canWrite('finance_accounts');

  const rows = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return accounts.filter(a => showOff || a.active);
    const byCode = new Map(accounts.map(a => [a.code, a]));
    const keep = new Set<string>();
    accounts
      .filter(a => (showOff || a.active) && (a.code.startsWith(term) || a.name.toLowerCase().includes(term)))
      .forEach(a => {
        let cur: LedgerAccount | undefined = a;
        while (cur) { keep.add(cur.code); cur = cur.parentCode ? byCode.get(cur.parentCode) : undefined; }
      });
    return accounts.filter(a => keep.has(a.code));
  }, [accounts, q, showOff]);

  const toggle = async (a: LedgerAccount) => {
    setMenu(null);
    try {
      await financeService.updateAccount(a._id, { active: !a.active });
      enqueueSnackbar(`${a.code} ${a.name} switched ${a.active ? 'off' : 'on'}`, { variant: 'success' });
      reload();
    } catch (e: any) {
      enqueueSnackbar(e?.message || 'Could not change this account.', { variant: 'error' });
    }
  };

  return (
    <Box>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }} sx={{ mb: 2 }}>
        <TextField
          size="small"
          placeholder="Search by code or name"
          value={q}
          onChange={e => setQ(e.target.value)}
          sx={{ flex: 1 }}
          InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" color="action" /></InputAdornment> }}
        />
        <FormControlLabel
          control={<Switch size="small" checked={showOff} onChange={e => setShowOff(e.target.checked)} />}
          label={<Typography variant="body2">Show switched-off</Typography>}
        />
        {editable && (
          <Button variant="contained" disableElevation startIcon={<Add />} onClick={() => setDialog({ account: null })} sx={{ borderRadius: 2, whiteSpace: 'nowrap' }}>
            Add account
          </Button>
        )}
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <TableContainer>
          <Table size="small" sx={{ '& td, & th': { py: 0.875 } }}>
            <TableHead>
              <TableRow sx={{ bgcolor: alpha(theme.palette.text.primary, 0.02) }}>
                <TableCell>Account</TableCell>
                <TableCell sx={{ width: 170 }}>Normal balance</TableCell>
                <TableCell sx={{ width: 170 }} align="right">Balance</TableCell>
                {editable && <TableCell sx={{ width: 48 }} />}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && accounts.length === 0 ? <TableRowSkeletons columns={editable ? 4 : 3} rows={12} rowHeight={36} /> : rows.map(a => (
                <TableRow key={a._id} hover sx={{ bgcolor: a.depth === 0 ? alpha(theme.palette.text.primary, 0.02) : undefined }}>
                  <TableCell sx={{ pl: 1.5 + a.depth * 2.5 }}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <AccountLedgerLink accountId={a._id} code={a.code} name={a.name} bold={a.isGroup} muted={!a.active} />
                      {a.controlModule && <ToneChip tone="info" size="small" label={CONTROL_LABEL[a.controlModule]} />}
                      {!a.active && <ToneChip tone="neutral" size="small" label="Off" />}
                    </Stack>
                    {a.description && !a.isGroup && (
                      <Typography variant="caption" color="text.disabled" display="block" noWrap sx={{ maxWidth: 520 }}>{a.description}</Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {!a.isGroup && <Typography variant="body2" color="text.secondary">{a.normalBalance === 'DEBIT' ? 'Debit' : 'Credit'}</Typography>}
                  </TableCell>
                  <TableCell align="right"><Amount paisa={a.balancePaisa} fontWeight={a.isGroup ? 700 : 400} /></TableCell>
                  {editable && (
                    <TableCell align="right" sx={{ pr: 1 }}>
                      {!a.isGroup && (
                        <IconButton size="small" onClick={e => setMenu({ anchor: e.currentTarget, account: a })} aria-label={`Actions for ${a.code}`}>
                          <MoreVert fontSize="small" />
                        </IconButton>
                      )}
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Menu anchorEl={menu?.anchor} open={!!menu} onClose={() => setMenu(null)} {...elegantMenuProps}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }} transformOrigin={{ vertical: 'top', horizontal: 'right' }}>
        <MenuItem onClick={() => { setDialog({ account: menu!.account }); setMenu(null); }}>
          <ListItemIcon><EditOutlined /></ListItemIcon>
          <ListItemText primary="Edit" />
        </MenuItem>
        {menu && !menu.account.controlModule && (
          <MenuItem onClick={() => toggle(menu.account)}>
            <ListItemIcon>{menu.account.active ? <ToggleOffOutlined /> : <ToggleOnOutlined />}</ListItemIcon>
            <ListItemText primary={menu.account.active ? 'Switch off' : 'Switch on'} />
          </MenuItem>
        )}
      </Menu>

      <AccountDialog open={!!dialog} account={dialog?.account ?? null} accounts={accounts} onClose={() => setDialog(null)} onSaved={reload} />
    </Box>
  );
};

export default ChartOfAccountsPanel;
